/**
 * The evidence tabs of Final Report v4: Gaps, Unverifiable, Access, PA = NO,
 * Write-downs.
 *
 * Each answers one question about the same rows.
 *
 *  - Gaps: which months Adwin invoiced that Raketech's files do not describe,
 *    and why — written down to nil after being reported, moved to another
 *    account id, or simply absent — month by month, with the deal code the
 *    invoice was raised under.
 *  - Unverifiable: which accounts' figures neither party can test at source,
 *    and on whose say-so — Raketech's own comment that a figure came "from
 *    operator invoices" or "from Finance", a closed account, no Adwin login.
 *  - Access: the accounts Adwin has no login for, set against Raketech's own
 *    Partner Account classification and clause 6.3, with what stands on them.
 *  - PA = NO: the accounts Raketech classifies as not Partner Accounts that
 *    took Adwin's players after 1 March 2023, with first and last acquisition
 *    on Raketech's own monthly reports — and how many of them match Adwin's
 *    invoicing to the cent, against how many Partner Accounts do.
 *  - Write-downs: the accounts Raketech reported, invoiced Adwin on, and later
 *    reduced or nulled, where Adwin holds nothing of its own to test them by.
 *
 * The last two are built to be sent: filtered, sorted, exported, and saved as
 * a shared snapshot of exactly the selection shown.
 */

import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Download, Eye, EyeOff, Lock, Scale, Share2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import type { AccessStatusResponse } from '@shared/raketech/accessStatus.types';
import type { ClaimRow, ClaimSummary } from '../../types/raketechFinalReportV4.types';
import { CLAIM_SOURCE_LABELS } from '../../types/raketechFinalReportV4.types';
import type { SharedV3Payload } from '../../lib/raketechShareV3';

import {
  VERIFY_LABELS, matchesToTheCent, noAccess, paOf, raketechComments, statusOf, theirCurrentFigure, verifyReasons,
  type AccessInfo, type Tone, type VerifyReason,
} from '../../lib/raketechAccess';
import { ftdSpan, WRITE_DOWN_DEFAULT_MIN, writeDownOf } from '../../lib/raketechFocus';

/** What a tab hands the page when the user asks to save and share its selection:
 *  the rows in the order shown, the purpose, and the access flags the tab drew
 *  from the no-login list, so the shared page can show the same flags. */
export type ShareRequest = {
  rows: ClaimRow[];
  focus: NonNullable<SharedV3Payload['focus']>;
  flags: Record<string, { noLogin: boolean; shared: boolean }>;
};

const flagsFor = (rows: ClaimRow[], access: Map<string, AccessInfo>): ShareRequest['flags'] =>
  Object.fromEntries(rows.map((r) => { const info = access.get(r.accountId); return [r.accountId, { noLogin: noAccess(info), shared: !!info?.shared }]; }));

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const fmtInt = (v: number) => v.toLocaleString('en-US');
const fmtEUR = (v: number) =>
  `€${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtEURExact = (v: number) =>
  `€${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMonth = (m: string) => {
  const [y, mm] = m.split('-');
  const d = new Date(Number(y), Number(mm) - 1, 1);
  return isNaN(d.getTime()) ? m : d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};

/** Same key the claim uses to index brands by month. */
const brandKey = (s: string): string =>
  (s || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace(/(casino|partners|affiliates)$/, '');

function downloadCsv(name: string, header: string[], rows: (string | number | null | undefined)[][]) {
  const cell = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(cell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length} rows`);
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const Explainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3">
    <p className="text-xs text-zinc-400 leading-relaxed">{children}</p>
  </div>
);

const TONES = {
  red: 'border-red-500/30 bg-red-500/10 text-red-400',
  amber: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  sky: 'border-sky-500/30 bg-sky-500/10 text-sky-400',
  emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  zinc: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400',
} as const satisfies Record<Tone, string>;

const Stat: React.FC<{ label: string; value: string; hint?: string; tone: Tone; icon: React.ReactNode }> =
  ({ label, value, hint, tone, icon }) => (
    <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] text-zinc-500 uppercase tracking-wider">{label}</span>
        <span className={`p-1.5 rounded-lg border ${TONES[tone]}`}>{icon}</span>
      </div>
      <p className="text-lg font-semibold text-white tabular-nums">{value}</p>
      {hint && <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>}
    </div>
  );

const Tag: React.FC<{ tone: Tone; children: React.ReactNode; title?: string }> = ({ tone, children, title }) => (
  <span title={title} className={`inline-block px-1.5 py-0.5 rounded border text-[10px] leading-tight ${TONES[tone]}`}>
    {children}
  </span>
);

const ExportButton: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
  >
    <Download className="h-3.5 w-3.5" />
    {label}
  </button>
);

const Th: React.FC<{ children?: React.ReactNode; right?: boolean }> = ({ children, right }) => (
  <th className={`px-3 py-2 font-medium text-zinc-500 ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

// ---------------------------------------------------------------------------
// Gaps
// ---------------------------------------------------------------------------

type GapKind = 'written_down' | 'shifted' | 'moved' | 'absent';

/** What each kind of gap is, in one line, and what Adwin asks Raketech for it. */
const GAP_LABELS: Record<GapKind, { short: string; tone: Tone; long: string; ask: string | null }> = {
  written_down: {
    short: 'Reported, then credited nil',
    tone: 'red',
    long: 'Raketech\'s monthly report carried this month and Adwin invoiced on it. Raketech\'s June 2025 file now shows nothing for it and credits the account below what it reported. The month is not missing from their files; they took it out.',
    ask: 'The source data behind removing this month from the account.',
  },
  shifted: {
    short: 'Booked in the month next door',
    tone: 'zinc',
    long: 'Raketech\'s later files show nothing for this month, but in the month before or after they carry more than Adwin invoiced there — by at least this amount. Raketech books the earnings a month earlier or later than its monthly report did. The account total is unaffected.',
    ask: null,
  },
  moved: {
    short: 'Same brand under another id',
    tone: 'sky',
    long: 'Raketech\'s later files carry this brand in this month, but under a different account id. The earnings exist; they have moved between accounts.',
    ask: 'Confirmation that this month\'s earnings now sit under the other account id, and why.',
  },
  absent: {
    short: 'Absent from their files',
    tone: 'amber',
    long: 'Raketech\'s own monthly report carried this month — Adwin invoiced on it — but neither the June 2025 file nor the July 2026 report has anything for the brand in the month, under any account. Their later files are incomplete here.',
    ask: 'This month\'s earnings for the account, which Raketech\'s own monthly report carried.',
  },
};

const GAP_ORDER: GapKind[] = ['written_down', 'absent', 'moved', 'shifted'];

interface GapMonth {
  month: string;
  invoiced: number;
  codes: string[];
  /** Other account ids carrying the brand that month, with their figure. */
  elsewhere: { id: string; amount: number }[];
  /** For a shifted month: the neighbour that carries the surplus. */
  spill: { month: string; above: number }[];
  kind: GapKind;
}

/** The months to show in an account's drilldown: every gap month with the
 *  month either side for context, in order, with a marker where the run breaks. */
const contextMonths = (gaps: string[], all: string[]): (string | null)[] => {
  const adj = (m: string, d: number) => { const [y, mm] = m.split('-').map(Number); const dt = new Date(y, mm - 1 + d, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`; };
  const want = new Set(gaps.flatMap((m) => [adj(m, -1), m, adj(m, 1)]));
  const months = all.filter((m) => want.has(m)).sort();
  const out: (string | null)[] = [];
  months.forEach((m, i) => { if (i > 0 && adj(months[i - 1], 1) !== m) out.push(null); out.push(m); });
  return out;
};

export const GapsTab: React.FC<{ rows: ClaimRow[]; summary: ClaimSummary; lastMonth: string }> = ({ rows, summary, lastMonth }) => {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [showPartial, setShowPartial] = useState(false);
  const [kindFilter, setKindFilter] = useState<GapKind | 'all'>('all');
  const [hideShifted, setHideShifted] = useState(true);

  // brand|month -> account id -> Raketech's figure for it that month
  const whereElse = useMemo(() => {
    const idx = new Map<string, Map<string, number>>();
    for (const r of rows) {
      const keys = [...new Set(r.brands.map(brandKey).filter(Boolean))];
      for (const m of r.monthly) {
        if (m.earned <= 0) continue;
        for (const k of keys) {
          const key = `${k}|${m.month}`;
          if (!idx.has(key)) idx.set(key, new Map());
          idx.get(key)!.set(r.accountId, (idx.get(key)!.get(r.accountId) || 0) + m.earned);
        }
      }
    }
    return idx;
  }, [rows]);

  const gapRows = useMemo(() => {
    const out: { row: ClaimRow; months: GapMonth[]; total: number; kinds: Record<GapKind, number>; context: (string | null)[] }[] = [];
    for (const r of rows) {
      if (r.coverage.gapMonths <= 0) continue;
      const keys = [...new Set(r.brands.map(brandKey).filter(Boolean))];
      const months: GapMonth[] = [];
      const byMonth = new Map(r.monthly.map((m) => [m.month, m]));
      const adjacent = (month: string): string[] => {
        const [y, mm] = month.split('-').map(Number);
        const d = (delta: number) => { const dt = new Date(y, mm - 1 + delta, 1); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`; };
        return [d(-1), d(1)];
      };
      for (const m of r.monthly) {
        if (m.month > lastMonth || m.invoiced <= 0 || m.earned !== 0) continue;
        const codes = r.invoiceEntries
          .filter((e) => e.monthly.some((x) => x.month === m.month && x.revenue > 0))
          .map((e) => String(e.label).split('—')[0].trim());
        const elsewhere = [...new Map(keys.flatMap((k) => [...(whereElse.get(`${k}|${m.month}`) || [])])).entries()]
          .filter(([id]) => id !== r.accountId)
          .map(([id, amount]) => ({ id, amount }));
        // Their report carrying, next door, more than was invoiced there by at
        // least this month's invoice is a month booked late or early, not a gap.
        const spill = adjacent(m.month)
          .map((am) => { const a = byMonth.get(am); return { month: am, above: Math.max(0, (a?.earned || 0) - (a?.invoiced || 0)) }; })
          .filter((s) => s.above > 0);
        const spillTotal = spill.reduce((s, x) => s + x.above, 0);
        // "Moved" only where the other account carries at least half of what
        // was invoiced here; a few euros under another id is not the money.
        const elsewhereTotal = elsewhere.reduce((s, x) => s + x.amount, 0);
        const kind: GapKind = r.reportedVsCredited > 0 && m.month <= '2024-11'
          ? 'written_down'
          : spillTotal >= m.invoiced * 0.8 ? 'shifted'
            : elsewhereTotal >= m.invoiced * 0.5 ? 'moved' : 'absent';
        months.push({ month: m.month, invoiced: m.invoiced, codes: [...new Set(codes)], elsewhere, spill, kind });
      }
      if (months.length === 0) continue;
      const kinds: Record<GapKind, number> = { written_down: 0, shifted: 0, moved: 0, absent: 0 };
      for (const g of months) kinds[g.kind] += g.invoiced;
      out.push({
        row: r, months, total: months.reduce((s, g) => s + g.invoiced, 0), kinds,
        context: contextMonths(months.map((m) => m.month), r.monthly.filter((m) => m.month <= lastMonth).map((m) => m.month)),
      });
    }
    return out.sort((a, b) => b.total - a.total);
  }, [rows, whereElse, lastMonth]);

  const totals = useMemo(() => {
    const t = { months: 0, invoiced: 0, written_down: 0, shifted: 0, moved: 0, absent: 0, n: { written_down: 0, shifted: 0, moved: 0, absent: 0 } as Record<GapKind, number> };
    for (const g of gapRows) {
      t.months += g.months.length;
      t.invoiced += g.total;
      for (const m of g.months) { t[m.kind] += m.invoiced; t.n[m.kind] += 1; }
    }
    return t;
  }, [gapRows]);

  const shown = useMemo(() => gapRows
    .map((g) => ({ ...g, visible: g.months.filter((m) => (kindFilter === 'all' || m.kind === kindFilter) && (!hideShifted || m.kind !== 'shifted')) }))
    .filter((g) => g.visible.length > 0)
    .sort((a, b) => b.visible.reduce((s, m) => s + m.invoiced, 0) - a.visible.reduce((s, m) => s + m.invoiced, 0)), [gapRows, kindFilter, hideShifted]);
  const shownTotal = shown.reduce((s, g) => s + g.visible.reduce((a, m) => a + m.invoiced, 0), 0);
  const shownMonths = shown.reduce((s, g) => s + g.visible.length, 0);

  const partialRows = useMemo(() => rows
    .filter((r) => r.coverage.partialMonths > 0)
    .map((r) => ({
      row: r,
      months: r.monthly.filter((m) => m.month <= lastMonth && m.invoiced > 0 && m.earned > 0 && m.earned < m.invoiced * 0.5),
    }))
    .filter((x) => x.months.length > 0)
    .sort((a, b) => b.row.coverage.partialWedge - a.row.coverage.partialWedge), [rows, lastMonth]);

  const toggle = (key: string) => setOpen((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const expandAll = () => setOpen(new Set(shown.map((g) => g.row.key)));
  const collapseAll = () => setOpen(new Set());

  const exportGaps = () => downloadCsv(
    `raketech-v4-gap-months-${lastMonth}.csv`,
    ['Account', 'Partner', 'Brands', 'Month', 'Invoiced (their monthly report)', 'Deal code(s)', 'June 2025 file', 'July 2026 report', 'Kind', 'Same brand reported that month under', 'Neighbouring month carrying the surplus', 'What Adwin asks', 'Raketech comment'],
    shown.flatMap((g) => g.visible.map((m) => [
      g.row.accountId, g.row.partner, g.row.brands.join(' / '), m.month, m.invoiced.toFixed(2), m.codes.join(' | '), '0.00', '0.00',
      GAP_LABELS[m.kind].short, m.elsewhere.map((e) => `${e.id} (${e.amount.toFixed(2)})`).join(' '),
      m.spill.map((s) => `${s.month} +${s.above.toFixed(2)}`).join(' '), GAP_LABELS[m.kind].ask || '', raketechComments(g.row).join(' | '),
    ])),
  );

  const kindCard = (k: GapKind) => (
    <button
      key={k}
      onClick={() => setKindFilter((v) => (v === k ? 'all' : k))}
      title={GAP_LABELS[k].long}
      className={`text-left bg-[hsl(225,15%,10%)] rounded-xl border p-4 transition-colors ${kindFilter === k ? 'border-amber-500/50' : 'border-[hsl(225,15%,18%)] hover:border-[hsl(225,15%,28%)]'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] text-zinc-500 uppercase tracking-wider">{GAP_LABELS[k].short}</span>
        <span className={`p-1.5 rounded-lg border ${TONES[GAP_LABELS[k].tone]}`}><Scale className="h-4 w-4" /></span>
      </div>
      <p className="text-lg font-semibold text-white tabular-nums">{fmtEUR(totals[k])}</p>
      <p className="text-[10px] text-zinc-500 mt-0.5">{totals.n[k]} month{totals.n[k] === 1 ? '' : 's'} · {GAP_LABELS[k].ask ? 'to put to Raketech' : 'not a question'}</p>
    </button>
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3 space-y-2 text-xs text-zinc-400 leading-relaxed">
        <p>
          <strong className="text-zinc-200">What a gap month is.</strong> Adwin invoiced every month on Raketech's own monthly
          Commission report. A gap month is one of those months where Raketech's later files — the June 2025 reconciliation
          and the July 2026 report — show <strong className="text-zinc-200">nothing</strong> for the account: their report
          said Adwin earned it, Adwin invoiced it, and their later files no longer have it.
        </p>
        <p>
          <strong className="text-zinc-200">How the claim treats them.</strong> At nil, in Raketech's favour: the account's
          figure on the other tabs already excludes these months. They are listed here because each is a month Raketech
          itself reported and later dropped, and because the reason differs. Each month is sorted into one of four kinds;
          three are questions for Raketech, one is not. Click a card to see only that kind. Open an account to see the gap
          months beside the months around them, with what each of Raketech's files carries.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Gap months" value={fmtInt(totals.months)} hint={`across ${gapRows.length} accounts · ${fmtEUR(totals.invoiced)} invoiced`} tone="zinc" icon={<Scale className="h-4 w-4" />} />
        {GAP_ORDER.map(kindCard)}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
        {GAP_ORDER.map((k) => (
          <div key={k} className="bg-[hsl(225,15%,10%)] rounded-lg border border-[hsl(225,15%,16%)] p-3">
            <div className="mb-1"><Tag tone={GAP_LABELS[k].tone}>{GAP_LABELS[k].short}</Tag></div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">{GAP_LABELS[k].long}</p>
            <p className="text-[11px] mt-1.5 leading-relaxed">
              <span className="text-zinc-500">Adwin asks: </span>
              <span className={GAP_LABELS[k].ask ? 'text-zinc-200' : 'text-zinc-600'}>{GAP_LABELS[k].ask || 'nothing — the money is in the month next door.'}</span>
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Toggle label="Hide months booked next door" on={hideShifted} onChange={setHideShifted} title="Leave out the months Raketech books a month earlier or later; the account total is unaffected" />
        {kindFilter !== 'all' && (
          <button onClick={() => setKindFilter('all')} className="px-3 py-1.5 text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-400">
            Showing: {GAP_LABELS[kindFilter].short} · clear
          </button>
        )}
        <span className="text-[11px] text-zinc-500">{shownMonths} months on {shown.length} accounts · {fmtEURExact(shownTotal)} invoiced</span>
        <div className="flex-1" />
        <button onClick={open.size > 0 ? collapseAll : expandAll} className="px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors">
          {open.size > 0 ? 'Collapse all' : 'Open all accounts'}
        </button>
        <button onClick={() => setShowPartial((v) => !v)} className="px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors">
          {showPartial ? 'Hide' : 'Show'} months described at under half ({summary.coverage.partialMonths})
        </button>
        <ExportButton onClick={exportGaps} label="Export gap months" />
      </div>

      <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[hsl(225,15%,12%)]">
              <tr>
                <Th />
                <Th>Account</Th>
                <Th>Partner / brand</Th>
                <Th right>Gap months</Th>
                <Th right>Invoiced in them</Th>
                <Th>Kind</Th>
                <Th right>Account overall</Th>
                <Th>Raketech's own words</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(225,15%,14%)]">
              {shown.map((g) => {
                const isOpen = open.has(g.row.key);
                const visTotal = g.visible.reduce((s, m) => s + m.invoiced, 0);
                const visKinds = GAP_ORDER.map((k) => [k, g.visible.filter((m) => m.kind === k).reduce((s, m) => s + m.invoiced, 0)] as [GapKind, number]).filter(([, v]) => v > 0);
                const byMonth = new Map(g.row.monthly.map((m) => [m.month, m]));
                const gapByMonth = new Map(g.months.map((m) => [m.month, m]));
                return (
                  <React.Fragment key={g.row.key}>
                    <tr className="hover:bg-[hsl(225,15%,12%)] cursor-pointer align-top" onClick={() => toggle(g.row.key)}>
                      <td className="px-3 py-2 text-zinc-500 w-6">{isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                      <td className="px-3 py-2 text-zinc-300 tabular-nums">{g.row.accountId}</td>
                      <td className="px-3 py-2">
                        <div className="text-zinc-200">{g.row.brands.slice(0, 2).join(' / ') || '—'}</div>
                        <div className="text-[10px] text-zinc-500">{g.row.partner}</div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{g.visible.length}<div className="text-[10px] text-zinc-600">{g.visible.length === 1 ? fmtMonth(g.visible[0].month) : `${fmtMonth(g.visible[0].month)} – ${fmtMonth(g.visible[g.visible.length - 1].month)}`}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-200">{fmtEURExact(visTotal)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {visKinds.map(([k, v]) => (
                            <Tag key={k} tone={GAP_LABELS[k].tone} title={GAP_LABELS[k].long}>{GAP_LABELS[k].short}{visKinds.length > 1 ? ` ${fmtEUR(v)}` : ''}</Tag>
                          ))}
                        </div>
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${g.row.underpaid > 1 ? 'text-amber-400' : g.row.underpaid < -1 ? 'text-red-400' : 'text-zinc-500'}`} title="The account's position on the claim, all months: their figure against Adwin's invoicing">
                        {g.row.underpaid > 1 ? 'invoiced above theirs' : g.row.underpaid < -1 ? 'Adwin short' : 'level'} {fmtEUR(Math.abs(g.row.underpaid))}
                      </td>
                      <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[260px] truncate" title={raketechComments(g.row).join(' | ')}>
                        {raketechComments(g.row).join(' · ') || '—'}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[hsl(225,15%,8%)]">
                        <td />
                        <td colSpan={7} className="px-3 py-3">
                          <table className="w-full max-w-5xl text-[11px]">
                            <thead>
                              <tr className="text-zinc-600">
                                <th className="text-left py-0.5 pr-3 font-medium">Month</th>
                                <th className="text-right py-0.5 px-3 font-medium">Invoiced<div className="text-[9px] font-normal">on their monthly report</div></th>
                                <th className="text-right py-0.5 px-3 font-medium">June 2025 file</th>
                                <th className="text-right py-0.5 px-3 font-medium">July 2026 report</th>
                                <th className="text-left py-0.5 px-3 font-medium">Deal code invoiced under</th>
                                <th className="text-left py-0.5 px-3 font-medium">Kind</th>
                                <th className="text-left py-0.5 pl-3 font-medium">Where the money is · what Adwin asks</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[hsl(225,15%,12%)]">
                              {g.context.map((month, i) => {
                                if (month === null) return <tr key={`gap-${i}`}><td colSpan={7} className="py-0.5 text-center text-zinc-700">···</td></tr>;
                                const m = byMonth.get(month)!;
                                const gap = gapByMonth.get(month);
                                const dim = !gap;
                                return (
                                  <tr key={month} className={gap ? (gap.kind === 'shifted' ? 'bg-zinc-500/[0.06]' : 'bg-red-500/[0.06]') : ''}>
                                    <td className={`py-1.5 pr-3 whitespace-nowrap ${dim ? 'text-zinc-500' : 'text-zinc-200 font-medium'}`}>{fmtMonth(month)}{gap && <span className="ml-1 text-[9px] uppercase tracking-wide text-red-400/80">gap</span>}</td>
                                    <td className={`py-1.5 px-3 text-right tabular-nums ${dim ? 'text-zinc-500' : 'text-zinc-100'}`}>{m.invoiced ? fmtEURExact(m.invoiced) : '—'}</td>
                                    <td className={`py-1.5 px-3 text-right tabular-nums ${gap ? 'text-red-400' : m.juneRecon > m.invoiced + 1 ? 'text-emerald-400' : 'text-zinc-400'}`}>{gap ? 'nothing' : m.juneRecon ? fmtEURExact(m.juneRecon) : '—'}</td>
                                    <td className={`py-1.5 px-3 text-right tabular-nums ${gap ? 'text-red-400' : m.restated > m.invoiced + 1 ? 'text-emerald-400' : 'text-zinc-400'}`}>{gap ? 'nothing' : m.restated ? fmtEURExact(m.restated) : '—'}</td>
                                    <td className="py-1.5 px-3 text-amber-400/80 break-all max-w-[220px]">{gap ? gap.codes.join(', ') || '—' : ''}</td>
                                    <td className="py-1.5 px-3">{gap && <Tag tone={GAP_LABELS[gap.kind].tone} title={GAP_LABELS[gap.kind].long}>{GAP_LABELS[gap.kind].short}</Tag>}</td>
                                    <td className="py-1.5 pl-3 text-zinc-400 leading-relaxed">
                                      {gap && gap.kind === 'shifted' && gap.spill.map((s) => <div key={s.month}>{fmtMonth(s.month)} carries <span className="text-emerald-400">{fmtEURExact(s.above)}</span> above what was invoiced there</div>)}
                                      {gap && gap.kind === 'moved' && gap.elsewhere.map((e) => <div key={e.id}>account <span className="text-sky-400">{e.id}</span> carries <span className="text-sky-400">{fmtEURExact(e.amount)}</span> for the brand that month</div>)}
                                      {gap && gap.kind === 'written_down' && <div>June 2025 file credits the account <span className="text-red-400">{fmtEURExact(g.row.reportedVsCredited)}</span> below what it reported</div>}
                                      {gap && gap.kind === 'absent' && (gap.elsewhere.length === 0
                                        ? <div>no Raketech file carries the brand in this month under any account</div>
                                        : <div>no Raketech file carries it here; {gap.elsewhere.map((e) => `account ${e.id} has only ${fmtEURExact(e.amount)} for the brand that month`).join(', ')}</div>)}
                                      {gap && GAP_LABELS[gap.kind].ask && <div className="text-zinc-500 mt-0.5">Ask: {GAP_LABELS[gap.kind].ask}</div>}
                                      {!gap && m.earned > 0 && m.earned > m.invoiced + 1 && <span className="text-zinc-600">their figure above the invoice by {fmtEURExact(m.earned - m.invoiced)}</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {shown.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">{gapRows.length === 0 ? 'Every month Adwin invoiced is described by at least one Raketech source.' : 'No gap months of this kind.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showPartial && (
        <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[hsl(225,15%,15%)]">
            <p className="text-xs text-zinc-300">Months Raketech's later files describe at under half of what its own monthly report carried</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Not gaps: their files do have a figure for the month. The claim takes that figure as it stands; the months are listed because the drop is large enough to ask about. {fmtEUR(summary.coverage.partialWedge)} in all.</p>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[hsl(225,15%,12%)]">
              <tr><Th>Account</Th><Th>Brand</Th><Th>Month</Th><Th right>Invoiced</Th><Th right>Their figure now</Th><Th right>Drop</Th></tr>
            </thead>
            <tbody className="divide-y divide-[hsl(225,15%,14%)]">
              {partialRows.flatMap((p) => p.months.map((m) => (
                <tr key={`${p.row.key}|${m.month}`} className="hover:bg-[hsl(225,15%,12%)]">
                  <td className="px-3 py-1.5 text-zinc-300 tabular-nums">{p.row.accountId}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{p.row.brands[0] || p.row.partner}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{fmtMonth(m.month)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-zinc-200">{fmtEURExact(m.invoiced)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-zinc-400">{fmtEURExact(m.earned)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-amber-400">{fmtEURExact(m.invoiced - m.earned)}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Unverifiable
// ---------------------------------------------------------------------------

export const UnverifiableTab: React.FC<{
  rows: ClaimRow[];
  access: Map<string, AccessInfo>;
  lastMonth: string;
}> = ({ rows, access, lastMonth }) => {
  const [filter, setFilter] = useState<VerifyReason | 'all'>('all');

  const items = useMemo(() => rows
    .map((row) => ({ row, reasons: verifyReasons(row, access.get(row.accountId)), info: access.get(row.accountId) }))
    .filter((x) => x.reasons.length > 0 && (x.row.earnedTotal > 0 || x.row.invoicedTotal > 0))
    .sort((a, b) => b.row.earnedTotal - a.row.earnedTotal), [rows, access]);

  const shown = filter === 'all' ? items : items.filter((x) => x.reasons.includes(filter));

  const byReason = useMemo(() => {
    const out = {} as Record<VerifyReason, { accounts: number; earned: number; short: number }>;
    for (const r of Object.keys(VERIFY_LABELS) as VerifyReason[]) out[r] = { accounts: 0, earned: 0, short: 0 };
    for (const x of items) for (const r of x.reasons) {
      out[r].accounts += 1;
      out[r].earned += x.row.earnedTotal;
      out[r].short += Math.max(0, -x.row.underpaid);
    }
    return out;
  }, [items]);

  const exportRows = () => downloadCsv(
    `raketech-v4-unverifiable-${lastMonth}.csv`,
    ['Account', 'Partner', 'Brands', 'PA per Raketech', 'Status per Raketech', 'Why unverifiable', 'Raketech comment', 'No-login usernames', 'Figure relied on', 'Earned', 'Invoiced', 'Difference'],
    shown.map(({ row, reasons, info }) => [
      row.accountId, row.partner, row.brands.join(' / '), paOf(row), statusOf(row),
      reasons.map((r) => VERIFY_LABELS[r].short).join(' | '), raketechComments(row).join(' | '),
      (info?.noLoginUsernames || []).join(' '), CLAIM_SOURCE_LABELS[row.chosenSource],
      row.earnedTotal.toFixed(2), row.invoicedTotal.toFixed(2), (-row.underpaid).toFixed(2),
    ]),
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <Explainer>
        Accounts whose figures neither party can test at source, and on whose say-so they stand. The
        figures are still Raketech's own and remain claimable — but where Raketech's comment says a
        number came "from operator invoices" or "from Finance", where the account is closed, or where
        Adwin was never given a login, there is nothing behind the number but the party that wrote it.
        These are the rows to ask for records on, and the rows where clause 6.3 — access "in order to
        ensure the rightful payment of the fees agreed" — does its work.
      </Explainer>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {(Object.keys(VERIFY_LABELS) as VerifyReason[]).map((r) => (
          <button key={r} onClick={() => setFilter(filter === r ? 'all' : r)} className={`text-left rounded-xl border p-4 transition-colors ${filter === r ? 'border-amber-500/40 bg-amber-500/5' : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] hover:bg-[hsl(225,15%,12%)]'}`} title={VERIFY_LABELS[r].long}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-zinc-500 uppercase tracking-wider">{VERIFY_LABELS[r].short}</span>
              <span className={`p-1.5 rounded-lg border ${TONES[VERIFY_LABELS[r].tone]}`}>{r === 'no_login' ? <Lock className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</span>
            </div>
            <p className="text-lg font-semibold text-white tabular-nums">{byReason[r].accounts}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">{fmtEUR(byReason[r].earned)} on their figures · {fmtEUR(byReason[r].short)} short</p>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[11px] text-zinc-500">
          {shown.length} accounts{filter !== 'all' ? ` · ${VERIFY_LABELS[filter].short}` : ''} · click a card to filter, again to clear
        </p>
        <ExportButton onClick={exportRows} label="Export" />
      </div>

      <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[hsl(225,15%,12%)]">
              <tr>
                <Th>Account</Th>
                <Th>Partner / brand</Th>
                <Th>Why it cannot be verified</Th>
                <Th>Raketech's own words</Th>
                <Th>Relied on</Th>
                <Th right>Their figure</Th>
                <Th right>Invoiced</Th>
                <Th right>Difference</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(225,15%,14%)]">
              {shown.map(({ row, reasons, info }) => (
                <tr key={row.key} className="hover:bg-[hsl(225,15%,12%)] align-top">
                  <td className="px-3 py-2 text-zinc-300 tabular-nums">
                    {row.accountId}
                    <div className="text-[10px] text-zinc-600">PA {paOf(row)}{statusOf(row) ? ` · ${statusOf(row)}` : ''}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-200">{row.brands.slice(0, 2).join(' / ') || '—'}</div>
                    <div className="text-[10px] text-zinc-500">{row.partner}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {reasons.map((r) => <Tag key={r} tone={VERIFY_LABELS[r].tone} title={VERIFY_LABELS[r].long}>{VERIFY_LABELS[r].short}</Tag>)}
                    </div>
                    {info && info.noLoginUsernames.length > 0 && (
                      <div className="text-[10px] text-zinc-500 mt-1">under {info.noLoginUsernames.join(', ')}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[280px]">
                    {raketechComments(row).join(' · ') || '—'}
                    {info?.verifiedComment && <div className="text-zinc-600 mt-0.5">Adwin: {info.verifiedComment}</div>}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-zinc-400">{CLAIM_SOURCE_LABELS[row.chosenSource]}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{fmtEUR(row.earnedTotal)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtEUR(row.invoicedTotal)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${row.underpaid < -1 ? 'text-red-400' : row.underpaid > 1 ? 'text-amber-400' : 'text-zinc-600'}`}>{fmtEUR(-row.underpaid)}</td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">Nothing in this category.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Access
// ---------------------------------------------------------------------------

export const AccessTab: React.FC<{
  rows: ClaimRow[];
  access: Map<string, AccessInfo>;
  raw: AccessStatusResponse | null;
  lastMonth: string;
}> = ({ rows, access, raw, lastMonth }) => {
  const [paFilter, setPaFilter] = useState<'all' | 'yes' | 'no'>('all');

  const items = useMemo(() => rows
    .map((row) => ({ row, info: access.get(row.accountId) }))
    .filter((x) => noAccess(x.info))
    .map((x) => ({ ...x, pa: paOf(x.row), reasons: verifyReasons(x.row, x.info) }))
    .sort((a, b) => b.row.earnedTotal - a.row.earnedTotal), [rows, access]);

  const shown = paFilter === 'all' ? items : items.filter((x) => x.pa === paFilter);

  const totals = useMemo(() => {
    const t = { accounts: items.length, earned: 0, invoiced: 0, short: 0, paYes: 0, paNo: 0, paYesEarned: 0, paNoEarned: 0 };
    for (const x of items) {
      t.earned += x.row.earnedTotal;
      t.invoiced += x.row.invoicedTotal;
      t.short += Math.max(0, -x.row.underpaid);
      if (x.pa === 'yes') { t.paYes += 1; t.paYesEarned += x.row.earnedTotal; }
      if (x.pa === 'no') { t.paNo += 1; t.paNoEarned += x.row.earnedTotal; }
    }
    return t;
  }, [items]);

  // No-login rows that could not be tied to any account in the report
  const unmatched = useMemo(() => {
    if (!raw) return [];
    const matched = new Set<string>();
    for (const a of access.values()) for (const o of a.noLoginOffers) matched.add(o);
    return raw.noLogin.filter((n) => !matched.has(n.offer));
  }, [raw, access]);

  const exportRows = () => downloadCsv(
    `raketech-v4-no-access-${lastMonth}.csv`,
    ['Account', 'Partner', 'Brands', 'PA per Raketech', 'Status', 'Deal codes without login', 'Raketech username', 'Raketech comment', 'Adwin note', 'Figure relied on', 'Their figure', 'Invoiced', 'Difference'],
    shown.map(({ row, info, pa }) => [
      row.accountId, row.partner, row.brands.join(' / '), pa, statusOf(row),
      (info?.noLoginOffers || []).join(' | '), (info?.noLoginUsernames || []).join(' '),
      raketechComments(row).join(' | '), info?.verifiedComment || '', CLAIM_SOURCE_LABELS[row.chosenSource],
      row.earnedTotal.toFixed(2), row.invoicedTotal.toFixed(2), (-row.underpaid).toFixed(2),
    ]),
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <Explainer>
        The accounts Adwin cannot open: those listed without a login when access was formally requested,
        those Adwin's own verification found not accessible, and those Raketech declared shared with other
        publishers — set against Raketech's own Partner Account classification in its July 2026 report.
        Each row says what tied the no-login list to the account (its Raketech username, its deal code,
        its brand); where two accounts agreed equally, both are named. Clause 6.3 obliges
        Raketech to grant access to the Partner Accounts "in order to ensure the rightful payment of
        the fees agreed", and clause 6.1.4 to record commission through a unique tracking id assigned
        to Adwin's New Users. Where Raketech itself marks an account <strong className="text-emerald-400">PA
        = YES</strong> and Adwin has no login, the refusal has no basis even on Raketech's reading. Where
        it marks <strong className="text-sky-400">PA = NO</strong>, that classification is Raketech's
        stated reason, applied after the fact and disputed in section 6 of Adwin's letter of 21 August.
        The usernames are Raketech's own network accounts: the traffic ran through accounts Raketech
        held and Adwin could not open.
      </Explainer>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Accounts Adwin cannot open" value={fmtInt(totals.accounts)} hint={`${raw?.noLogin.length ?? 0} deal codes on the no-login list · ${items.filter((x) => x.info?.shared).length} declared shared`} tone="red" icon={<Lock className="h-4 w-4" />} />
        <Stat label="On their figures" value={fmtEUR(totals.earned)} hint={`${fmtEUR(totals.invoiced)} invoiced · ${fmtEUR(totals.short)} short`} tone="zinc" icon={<Eye className="h-4 w-4" />} />
        <button onClick={() => setPaFilter(paFilter === 'yes' ? 'all' : 'yes')} className={`text-left rounded-xl border p-4 transition-colors ${paFilter === 'yes' ? 'border-amber-500/40 bg-amber-500/5' : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] hover:bg-[hsl(225,15%,12%)]'}`}>
          <div className="flex items-center justify-between mb-2"><span className="text-[11px] text-zinc-500 uppercase tracking-wider">Partner Account per Raketech</span><span className={`p-1.5 rounded-lg border ${TONES.emerald}`}><Shield className="h-4 w-4" /></span></div>
          <p className="text-lg font-semibold text-white tabular-nums">{totals.paYes}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">{fmtEUR(totals.paYesEarned)} · access owed on their own classification</p>
        </button>
        <button onClick={() => setPaFilter(paFilter === 'no' ? 'all' : 'no')} className={`text-left rounded-xl border p-4 transition-colors ${paFilter === 'no' ? 'border-amber-500/40 bg-amber-500/5' : 'border-[hsl(225,15%,18%)] bg-[hsl(225,15%,10%)] hover:bg-[hsl(225,15%,12%)]'}`}>
          <div className="flex items-center justify-between mb-2"><span className="text-[11px] text-zinc-500 uppercase tracking-wider">Not a PA per Raketech</span><span className={`p-1.5 rounded-lg border ${TONES.sky}`}><Shield className="h-4 w-4" /></span></div>
          <p className="text-lg font-semibold text-white tabular-nums">{totals.paNo}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">{fmtEUR(totals.paNoEarned)} · classification disputed, reserved</p>
        </button>
        <Stat label="Not tied to an account" value={fmtInt(unmatched.length)} hint="Deal codes on the list no report row carries" tone="zinc" icon={<Scale className="h-4 w-4" />} />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[11px] text-zinc-500">{shown.length} accounts{paFilter !== 'all' ? ` · PA = ${paFilter.toUpperCase()}` : ''}</p>
        <ExportButton onClick={exportRows} label="Export" />
      </div>

      <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[hsl(225,15%,12%)]">
              <tr>
                <Th>Account</Th>
                <Th>Partner / brand</Th>
                <Th>PA</Th>
                <Th>Held under</Th>
                <Th>Deal codes without login</Th>
                <Th>Raketech's own words</Th>
                <Th>Relied on</Th>
                <Th right>Their figure</Th>
                <Th right>Invoiced</Th>
                <Th right>Difference</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(225,15%,14%)]">
              {shown.map(({ row, info, pa }) => (
                <tr key={row.key} className="hover:bg-[hsl(225,15%,12%)] align-top">
                  <td className="px-3 py-2 text-zinc-300 tabular-nums">
                    {row.accountId}
                    {statusOf(row) && <div className="text-[10px] text-zinc-600">{statusOf(row)}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-zinc-200">{row.brands.slice(0, 2).join(' / ') || '—'}</div>
                    <div className="text-[10px] text-zinc-500">{row.partner}</div>
                  </td>
                  <td className="px-3 py-2">
                    {pa === 'yes' && <Tag tone="emerald" title="Raketech's own report classifies this as a Partner Account. Access is owed under clause 6.3 on their own reading.">PA = YES</Tag>}
                    {pa === 'no' && <Tag tone="sky" title="Raketech classifies this PA = NO. Adwin does not accept the classification; reserved.">PA = NO</Tag>}
                    {pa === 'mixed' && <Tag tone="amber">mixed</Tag>}
                    {pa === 'unknown' && <Tag tone="zinc">not in July report</Tag>}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-zinc-400 break-all">
                    {(info?.noLoginUsernames || []).join(', ') || (row.username || '—')}
                    {info && info.matchedOn.length > 0 && (
                      <div className="text-zinc-600 mt-0.5" title="What tied the no-login list to this account">
                        matched on {info.matchedOn.join(' + ')}
                      </div>
                    )}
                    {info?.ambiguous && info.ambiguous.length > 0 && (
                      <div className="text-amber-400/80 mt-0.5" title="Another account agreed equally on the list's username and code; the row is placed here and named there">
                        could also be {info.ambiguous.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-amber-400/80 break-all max-w-[240px]">
                    {(info?.noLoginOffers || []).join(', ') || '—'}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {info?.shared && <Tag tone="amber" title={VERIFY_LABELS.shared.long}>Raketech says shared</Tag>}
                      {info?.notAccessible && (info?.noLoginOffers.length ?? 0) === 0 && <Tag tone="red" title="Adwin's own verification note: the account could not be opened">Adwin: not accessible</Tag>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[260px]">
                    {raketechComments(row).join(' · ') || '—'}
                    {info?.verifiedComment && <div className="text-zinc-600 mt-0.5">Adwin: {info.verifiedComment}</div>}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-zinc-400">{CLAIM_SOURCE_LABELS[row.chosenSource]}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{fmtEUR(row.earnedTotal)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtEUR(row.invoicedTotal)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${row.underpaid < -1 ? 'text-red-400' : row.underpaid > 1 ? 'text-amber-400' : 'text-zinc-600'}`}>{fmtEUR(-row.underpaid)}</td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-zinc-500">{raw ? 'No accounts in this view.' : 'The access list has not loaded.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {unmatched.length > 0 && (
        <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[hsl(225,15%,15%)]">
            <p className="text-xs text-zinc-300">Deal codes on the no-access list that no account in this report carries</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Neither Raketech's reports nor Adwin's invoicing name these codes, so nothing stands on them here. They remain part of the access request.</p>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[hsl(225,15%,12%)]"><tr><Th>Deal code</Th><Th>Brand</Th><Th>Partner</Th><Th>Held under</Th></tr></thead>
            <tbody className="divide-y divide-[hsl(225,15%,14%)]">
              {unmatched.map((n) => (
                <tr key={n.offer} className="text-zinc-400">
                  <td className="px-3 py-1.5 text-amber-400/80 break-all">{n.offer}</td>
                  <td className="px-3 py-1.5">{n.brand}</td>
                  <td className="px-3 py-1.5 text-zinc-500">{n.partner}</td>
                  <td className="px-3 py-1.5 text-zinc-500">{n.username}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared pieces for the two "send this" tabs
// ---------------------------------------------------------------------------

type SortDir = 'asc' | 'desc';

function useSort<K extends string>(initial: K, initialDir: SortDir = 'desc') {
  const [key, setKey] = useState<K>(initial);
  const [dir, setDir] = useState<SortDir>(initialDir);
  const toggle = (k: K) => {
    if (k === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setKey(k); setDir('desc'); }
  };
  return { key, dir, toggle };
}

const SortTh: React.FC<{ label: string; k: string; sort: { key: string; dir: SortDir; toggle: (k: never) => void }; right?: boolean }> =
  ({ label, k, sort, right }) => (
    <th
      className={`px-3 py-2 font-medium cursor-pointer select-none whitespace-nowrap ${right ? 'text-right' : 'text-left'} ${sort.key === k ? 'text-amber-300' : 'text-zinc-500 hover:text-zinc-300'}`}
      onClick={() => (sort.toggle as (k: string) => void)(k)}
    >
      {label}
      {sort.key === k && (sort.dir === 'asc' ? <ArrowUp className="inline h-3 w-3 ml-1" /> : <ArrowDown className="inline h-3 w-3 ml-1" />)}
    </th>
  );

const NumberInput: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({ label, value, onChange }) => (
  <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg">
    <span className="text-zinc-500">{label}</span>
    <input
      type="number"
      min={0}
      step={100}
      value={value}
      onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
      className="w-24 bg-transparent text-zinc-200 outline-none tabular-nums"
    />
  </label>
);

const Toggle: React.FC<{ label: string; on: boolean; onChange: (v: boolean) => void; title?: string }> = ({ label, on, onChange, title }) => (
  <label title={title} className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
    <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} className="accent-amber-500" />
    {label}
  </label>
);

const ShareButton: React.FC<{ onClick: () => void; count: number }> = ({ onClick, count }) => (
  <button
    onClick={onClick}
    disabled={count === 0}
    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-200 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg transition-colors disabled:opacity-40"
  >
    <Share2 className="h-3.5 w-3.5" />
    Save &amp; share these {count}
  </button>
);

const cmp = (a: number | string | undefined, b: number | string | undefined, dir: SortDir) => {
  const x = a ?? (typeof b === 'number' ? -Infinity : ''), y = b ?? (typeof a === 'number' ? -Infinity : '');
  const r = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
  return dir === 'asc' ? r : -r;
};

const MonthDetail: React.FC<{ row: ClaimRow; columns: ('ftds' | 'invoiced' | 'june' | 'reported' | 'restated' | 'earned')[] }> = ({ row, columns }) => {
  const names: Record<string, string> = { ftds: 'FTDs', invoiced: 'Invoiced (their monthly report)', june: 'June 2025 file', reported: 'Reported at the time', restated: 'July 2026 report', earned: 'Relied on' };
  const months = row.monthly.filter((m) => m.invoiced || m.earned || m.juneRecon || m.restated || m.ftds);
  return (
    <table className="w-full max-w-3xl text-[11px]">
      <thead>
        <tr className="text-zinc-600">
          <th className="text-left py-0.5 pr-3 font-medium">Month</th>
          {columns.map((c) => <th key={c} className="text-right py-0.5 px-3 font-medium">{names[c]}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-[hsl(225,15%,12%)]">
        {months.map((m) => (
          <tr key={m.month} className="text-zinc-400">
            <td className="py-1 pr-3">{fmtMonth(m.month)}</td>
            {columns.map((c) => {
              const v = c === 'ftds' ? m.ftds : c === 'invoiced' ? m.invoiced : c === 'june' ? m.juneRecon : c === 'reported' ? m.juneReported : c === 'restated' ? m.restated : m.earned;
              return <td key={c} className={`py-1 px-3 text-right tabular-nums ${c === 'ftds' ? '' : v ? 'text-zinc-300' : 'text-zinc-700'}`}>{c === 'ftds' ? (v || '—') : v ? fmtEURExact(v) : '—'}</td>;
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ---------------------------------------------------------------------------
// PA = NO with players after 1 March 2023
// ---------------------------------------------------------------------------

type PaSortKey = 'earned' | 'invoiced' | 'difference' | 'ftds' | 'firstFtd' | 'lastFtd' | 'created' | 'account';

export const PaNoTab: React.FC<{ rows: ClaimRow[]; access: Map<string, AccessInfo>; lastMonth: string; onShare: (req: ShareRequest) => void }> =
  ({ rows, access, lastMonth, onShare }) => {
    const [minFigure, setMinFigure] = useState(0);
    const [withPlayers, setWithPlayers] = useState(true);
    const [exactOnly, setExactOnly] = useState(false);
    const [includeMixed, setIncludeMixed] = useState(false);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState<Set<string>>(new Set());
    const sort = useSort<PaSortKey>('earned');

    const base = useMemo(() => rows
      .filter((r) => { const pa = paOf(r); return pa === 'no' || (includeMixed && pa === 'mixed'); })
      .map((r) => {
        // "Their figure" is Raketech's current figure, not the claim's, which
        // may rest on Adwin's own data or on the Reported column.
        const theirs = theirCurrentFigure(r);
        return { row: r, pa: paOf(r), ftd: ftdSpan(r), exact: matchesToTheCent(r), theirs, diff: theirs - r.invoicedTotal, info: access.get(r.accountId), created: r.offers[0]?.created || '' };
      }), [rows, access, includeMixed]);

    const shown = useMemo(() => {
      const q = search.trim().toLowerCase();
      const list = base.filter((x) =>
        // "After March 2023": the account must have taken a player in April
        // 2023 or later, not merely in the first month of the period.
        (!withPlayers || (x.ftd.last ?? '') > '2023-03')
        && (!exactOnly || x.exact)
        && Math.max(x.theirs, x.row.invoicedTotal) >= minFigure
        && (!q || `${x.row.accountId} ${x.row.partner} ${x.row.brands.join(' ')}`.toLowerCase().includes(q)));
      const val = (x: typeof list[number]): number | string | undefined => ({
        earned: x.theirs, invoiced: x.row.invoicedTotal, difference: x.diff, ftds: x.ftd.total,
        firstFtd: x.ftd.first, lastFtd: x.ftd.last, created: x.created, account: x.row.accountId,
      })[sort.key];
      return list.sort((a, b) => cmp(val(a), val(b), sort.dir));
    }, [base, withPlayers, exactOnly, minFigure, search, sort.key, sort.dir]);

    // The comparison the suspicion rests on: how often Raketech's figure equals
    // Adwin's invoice to the cent, on these accounts against the Partner Accounts.
    const matchRates = useMemo(() => {
      const withData = (r: ClaimRow) => r.invoicedTotal > 0;
      const no = rows.filter((r) => paOf(r) === 'no' && withData(r));
      const yes = rows.filter((r) => paOf(r) === 'yes' && withData(r));
      const exact = (list: ClaimRow[]) => list.filter(matchesToTheCent).length;
      return { no: { n: no.length, exact: exact(no) }, yes: { n: yes.length, exact: exact(yes) } };
    }, [rows]);

    const totals = useMemo(() => ({
      earned: shown.reduce((s, x) => s + x.theirs, 0),
      invoiced: shown.reduce((s, x) => s + x.row.invoicedTotal, 0),
      ftds: shown.reduce((s, x) => s + x.ftd.total, 0),
      exact: shown.filter((x) => x.exact).length,
      noLogin: shown.filter((x) => noAccess(x.info)).length,
    }), [shown]);

    const toggleOpen = (k: string) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
    const selection = () => [
      'Raketech classification PA = NO' + (includeMixed ? ' or mixed' : ''),
      withPlayers ? 'acquisitions on Raketech\'s monthly reports in April 2023 or later' : null,
      exactOnly ? 'Raketech\'s figure equal to Adwin\'s invoicing to the cent' : null,
      minFigure > 0 ? `at least EUR ${minFigure.toLocaleString('en-GB')} on either side` : null,
      search.trim() ? `matching "${search.trim()}"` : null,
    ].filter(Boolean).join('; ');

    const exportRows = () => {
      downloadCsv(`raketech-v4-pa-no-${lastMonth}.csv`,
        ['Account', 'Partner', 'Brands', 'PA per Raketech', 'Created', 'Status', 'First FTD month', 'Last FTD month', 'FTDs', 'Months with FTDs', 'Raketech current figure', 'Invoiced by Adwin', 'Difference (their figure less invoiced)', 'Matches to the cent', 'Claim figure (Adwin own data where held)', 'No Adwin login', 'Raketech comment', 'Deal codes'],
        shown.map(({ row, pa, ftd, exact, theirs, diff, info, created }) => [
          row.accountId, row.partner, row.brands.join(' / '), pa, created.slice(0, 10), statusOf(row), ftd.first || '', ftd.last || '', ftd.total, ftd.months,
          theirs.toFixed(2), row.invoicedTotal.toFixed(2), diff.toFixed(2), exact ? 'yes' : 'no', row.earnedTotal.toFixed(2), noAccess(info) ? 'yes' : 'no',
          raketechComments(row).join(' | '), row.offers.map((o) => o.offer).join(' | '),
        ]));
      downloadCsv(`raketech-v4-pa-no-months-${lastMonth}.csv`,
        ['Account', 'Brand', 'Month', 'FTDs', 'Invoiced (their monthly report)', 'June 2025 file', 'July 2026 report', 'Relied on'],
        shown.flatMap(({ row }) => row.monthly.filter((m) => m.invoiced || m.earned || m.ftds).map((m) => [row.accountId, row.brands[0] || '', m.month, m.ftds, m.invoiced.toFixed(2), m.juneRecon.toFixed(2), m.restated.toFixed(2), m.earned.toFixed(2)])));
    };

    return (
      <div className="space-y-4 animate-fade-in">
        <Explainer>
          The accounts Raketech's July 2026 report classifies as <strong className="text-sky-400">PA = NO</strong> — its stated
          basis for treating them as outside the Agreement — that nonetheless took Adwin's players after 1 March 2023 on
          Raketech's own monthly reports. Clause 3.3.1 required every account carrying Adwin traffic from that date to be set
          up for Adwin, and clause 6.3 gives Adwin access to verify it. First and last acquisition months and the count come
          from the monthly reports Adwin invoiced on; there is no other source, because these are the accounts Adwin cannot
          open. Note the match rate: on these accounts Raketech's figure equals Adwin's invoice to the cent far more often than
          on the Partner Accounts — which is what a figure copied from the invoice, rather than taken from the account, looks like.
        </Explainer>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat label="Accounts shown" value={fmtInt(shown.length)} hint={`of ${base.length} PA = NO${includeMixed ? ' / mixed' : ''}`} tone="sky" icon={<Shield className="h-4 w-4" />} />
          <Stat label="On Raketech's figures" value={fmtEUR(totals.earned)} hint={`${fmtEUR(totals.invoiced)} invoiced by Adwin`} tone="zinc" icon={<Scale className="h-4 w-4" />} />
          <Stat label="Acquisitions" value={fmtInt(totals.ftds)} hint="On Raketech's monthly reports" tone="zinc" icon={<Eye className="h-4 w-4" />} />
          <Stat label="Match to the cent" value={`${matchRates.no.exact} of ${matchRates.no.n}`} hint={`PA = NO accounts with data · Partner Accounts: ${matchRates.yes.exact} of ${matchRates.yes.n}`} tone="amber" icon={<Scale className="h-4 w-4" />} />
          <Stat label="No Adwin login" value={fmtInt(totals.noLogin)} hint="of the accounts shown" tone="red" icon={<Lock className="h-4 w-4" />} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by partner, brand, id…"
            className="px-3 py-1.5 text-xs bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber-500/40 w-60" />
          <NumberInput label="Min EUR" value={minFigure} onChange={setMinFigure} />
          <Toggle label="With players after Mar 2023" on={withPlayers} onChange={setWithPlayers} title="Last acquisition on Raketech's monthly reports in April 2023 or later" />
          <Toggle label="Exact matches only" on={exactOnly} onChange={setExactOnly} title="Raketech's figure equals Adwin's invoicing to the cent" />
          <Toggle label="Include mixed PA" on={includeMixed} onChange={setIncludeMixed} title="Accounts where some deals are PA = YES and some PA = NO" />
          <div className="flex-1" />
          <ExportButton onClick={exportRows} label="Export" />
          <ShareButton count={shown.length} onClick={() => onShare({
            rows: shown.map((x) => x.row),
            flags: flagsFor(shown.map((x) => x.row), access),
            focus: {
              kind: 'pa_no_players',
              title: 'Accounts Raketech classifies as not Partner Accounts that took Adwin\'s players after 1 March 2023',
              note: 'Each account below is marked PA = NO in Raketech\'s July 2026 report. Each carries acquisitions of Adwin\'s players on Raketech\'s own monthly reports after 1 March 2023, with the first and last month shown. Adwin asks for the basis of the classification for each, and for access under clause 6.3. Where Raketech\'s figure equals Adwin\'s invoicing to the cent, that is noted: it is what a figure taken from the invoice rather than from the account looks like.',
              selection: selection(),
            },
          })} />
        </div>

        <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(225,15%,12%)]">
                <tr>
                  <Th />
                  <SortTh label="Account" k="account" sort={sort as never} />
                  <Th>Partner / brand</Th>
                  <SortTh label="Created" k="created" sort={sort as never} />
                  <SortTh label="First FTD" k="firstFtd" sort={sort as never} />
                  <SortTh label="Last FTD" k="lastFtd" sort={sort as never} />
                  <SortTh label="FTDs" k="ftds" sort={sort as never} right />
                  <SortTh label="Their figure" k="earned" sort={sort as never} right />
                  <SortTh label="Invoiced" k="invoiced" sort={sort as never} right />
                  <SortTh label="Difference" k="difference" sort={sort as never} right />
                  <Th>Flags</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                {shown.map(({ row, pa, ftd, exact, theirs, diff, info, created }) => (
                  <React.Fragment key={row.key}>
                    <tr className="hover:bg-[hsl(225,15%,12%)] cursor-pointer align-top" onClick={() => toggleOpen(row.key)}>
                      <td className="px-3 py-2 text-zinc-500 w-6">{open.has(row.key) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                      <td className="px-3 py-2 text-zinc-300 tabular-nums">{row.accountId}{pa === 'mixed' && <div className="text-[10px] text-amber-400/80">mixed PA</div>}{statusOf(row) && <div className="text-[10px] text-zinc-600">{statusOf(row)}</div>}</td>
                      <td className="px-3 py-2"><div className="text-zinc-200">{row.brands.slice(0, 2).join(' / ') || '—'}</div><div className="text-[10px] text-zinc-500">{row.partner}</div></td>
                      <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{created ? created.slice(0, 10) : '—'}</td>
                      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{ftd.first ? fmtMonth(ftd.first) : '—'}</td>
                      <td className="px-3 py-2 text-zinc-300 whitespace-nowrap">{ftd.last ? fmtMonth(ftd.last) : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{ftd.total ? fmtInt(ftd.total) : '—'}<div className="text-[10px] text-zinc-600">{ftd.months ? `${ftd.months} mo` : ''}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{fmtEUR(theirs)}{Math.abs(row.earnedTotal - theirs) > 1 && <div className="text-[10px] text-zinc-500" title="The claim's figure for the account, from Adwin's own data or the Reported column">claim {fmtEUR(row.earnedTotal)}</div>}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtEUR(row.invoicedTotal)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${diff > 1 ? 'text-red-400' : diff < -1 ? 'text-amber-400' : 'text-zinc-600'}`}>{fmtEUR(diff)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {exact && <Tag tone="amber" title="Raketech's figure equals Adwin's invoicing to the cent — a figure taken from the invoice, not the account">to the cent</Tag>}
                          {noAccess(info) && <Tag tone="red" title={VERIFY_LABELS.no_login.long}>no login</Tag>}
                          {info?.shared && <Tag tone="amber" title={VERIFY_LABELS.shared.long}>shared</Tag>}
                          {raketechComments(row).length > 0 && <Tag tone="zinc" title={raketechComments(row).join(' | ')}>comment</Tag>}
                        </div>
                      </td>
                    </tr>
                    {open.has(row.key) && (
                      <tr className="bg-[hsl(225,15%,8%)]"><td /><td colSpan={10} className="px-3 py-2">
                        <div className="text-[10px] text-zinc-500 mb-1">Deal codes: {row.offers.map((o) => o.offer).join(', ') || '—'}{info?.noLoginUsernames.length ? ` · held under ${info.noLoginUsernames.join(', ')}` : ''}</div>
                        <MonthDetail row={row} columns={['ftds', 'invoiced', 'june', 'restated', 'earned']} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {shown.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-500">No accounts match the filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

// ---------------------------------------------------------------------------
// Write-downs: reported, invoiced on, then reduced or nulled
// ---------------------------------------------------------------------------

type WdSortKey = 'writeDown' | 'reported' | 'current' | 'pct' | 'account' | 'invoiced';

export const WriteDownsTab: React.FC<{ rows: ClaimRow[]; access: Map<string, AccessInfo>; lastMonth: string; onShare: (req: ShareRequest) => void }> =
  ({ rows, access, lastMonth, onShare }) => {
    // Below this a reduction is rounding between two of Raketech's files, not
    // a write-down; the default keeps the list to what is worth asking about.
    const [minWriteDown, setMinWriteDown] = useState(WRITE_DOWN_DEFAULT_MIN);
    const [noOwnData, setNoOwnData] = useState(true);
    const [nulledOnly, setNulledOnly] = useState(false);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState<Set<string>>(new Set());
    const sort = useSort<WdSortKey>('writeDown');

    const base = useMemo(() => rows.map((row) => ({ row, ...writeDownOf(row), info: access.get(row.accountId) })).filter((x) => x.writeDown > 1), [rows, access]);

    const shown = useMemo(() => {
      const q = search.trim().toLowerCase();
      const list = base.filter((x) =>
        (!noOwnData || !x.ownData)
        && (!nulledOnly || x.nulled)
        && x.writeDown >= minWriteDown
        && (!q || `${x.row.accountId} ${x.row.partner} ${x.row.brands.join(' ')}`.toLowerCase().includes(q)));
      const val = (x: typeof list[number]): number | string => ({
        writeDown: x.writeDown, reported: x.reported, current: x.current, pct: x.pct, account: x.row.accountId, invoiced: x.row.invoicedRecon,
      })[sort.key];
      return list.sort((a, b) => cmp(val(a), val(b), sort.dir));
    }, [base, noOwnData, nulledOnly, minWriteDown, search, sort.key, sort.dir]);

    const totals = useMemo(() => ({
      writeDown: shown.reduce((s, x) => s + x.writeDown, 0),
      reportedToJune: shown.reduce((s, x) => s + x.reportedToJune, 0),
      juneToJuly: shown.reduce((s, x) => s + x.juneToJuly, 0),
      tail: shown.reduce((s, x) => s + x.tail, 0),
      nulled: shown.filter((x) => x.nulled).length,
      commented: shown.filter((x) => raketechComments(x.row).length > 0).length,
    }), [shown]);

    const toggleOpen = (k: string) => setOpen((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
    const selection = () => [
      'accounts Raketech reported, Adwin invoiced on, and Raketech later reduced',
      noOwnData ? 'where Adwin holds no operator-backend or manual figure of its own' : null,
      nulledOnly ? 'reduced to nil' : null,
      minWriteDown > 0 ? `reduction of at least EUR ${minWriteDown.toLocaleString('en-GB')}` : null,
      search.trim() ? `matching "${search.trim()}"` : null,
    ].filter(Boolean).join('; ');

    const exportRows = () => {
      downloadCsv(`raketech-v4-write-downs-${lastMonth}.csv`,
        ['Account', 'Partner', 'Brands', 'PA per Raketech', 'Invoiced Mar 2023-Nov 2024', 'Reported at the time (June file Reported)', 'June 2025 file credited', 'July 2026 report, Mar 2023-Nov 2024', 'Invoiced Dec 2024 on', 'July 2026 report, Dec 2024 on', 'Reported to June reduction', 'June to July reduction', 'July report below monthly reports Dec 2024 on', 'Total reduction', 'Reduction %', 'Nulled', 'Adwin holds own data', 'Difference whole period (their figure less invoiced)', 'Raketech comment'],
        shown.map((x) => [
          x.row.accountId, x.row.partner, x.row.brands.join(' / '), paOf(x.row), x.row.invoicedRecon.toFixed(2), x.reported.toFixed(2), x.june.toFixed(2), x.july.toFixed(2),
          x.row.invoicedPostRecon.toFixed(2), x.row.earnedTail.toFixed(2),
          x.reportedToJune.toFixed(2), x.juneToJuly.toFixed(2), x.tail.toFixed(2), x.writeDown.toFixed(2), (x.pct * 100).toFixed(1) + '%', x.nulled ? 'yes' : 'no', x.ownData ? 'yes' : 'no',
          (-x.row.underpaid).toFixed(2), raketechComments(x.row).join(' | '),
        ]));
      downloadCsv(`raketech-v4-write-downs-months-${lastMonth}.csv`,
        ['Account', 'Brand', 'Month', 'Invoiced (their monthly report)', 'Reported at the time', 'June 2025 file', 'July 2026 report'],
        shown.flatMap((x) => x.row.monthly.filter((m) => m.invoiced || m.juneRecon || m.restated || m.juneReported).map((m) => [x.row.accountId, x.row.brands[0] || '', m.month, m.invoiced.toFixed(2), m.juneReported.toFixed(2), m.juneRecon.toFixed(2), m.restated.toFixed(2)])));
    };

    return (
      <div className="space-y-4 animate-fade-in">
        <Explainer>
          Accounts where Raketech's own figure has moved over time: what its monthly reports said and Adwin invoiced on
          (<em>Reported</em>), what its June 2025 file then credited, and what its July 2026 report now carries — for the
          reconciled period and, from December 2024, against the monthly reports themselves. Each step down is measured on
          its own pair of Raketech columns, so nothing is counted twice. A month the July report books one month earlier or
          later than the monthly report did is netted first and is not a reduction. Where Adwin holds no operator-backend or manual figure,
          the only evidence for the reduction is Raketech's, and Raketech's own comment column is reproduced. The request on
          every row is the same: the source data behind the reduction.
        </Explainer>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Stat label="Accounts shown" value={fmtInt(shown.length)} hint={`of ${base.length} reduced`} tone="red" icon={<Scale className="h-4 w-4" />} />
          <Stat label="Total reduction" value={fmtEUR(totals.writeDown)} hint="Across the three steps" tone="red" icon={<Scale className="h-4 w-4" />} />
          <Stat label="Reported → June credited" value={fmtEUR(totals.reportedToJune)} hint="Inside their June 2025 file" tone="amber" icon={<Scale className="h-4 w-4" />} />
          <Stat label="June → July report" value={fmtEUR(totals.juneToJuly)} hint="Between their two files" tone="amber" icon={<Scale className="h-4 w-4" />} />
          <Stat label="Monthly reports → July, Dec 24 on" value={fmtEUR(totals.tail)} hint="Later months" tone="amber" icon={<Scale className="h-4 w-4" />} />
          <Stat label="Reduced to nil" value={fmtInt(totals.nulled)} hint={`${totals.commented} carry a Raketech comment`} tone="zinc" icon={<EyeOff className="h-4 w-4" />} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by partner, brand, id…"
            className="px-3 py-1.5 text-xs bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber-500/40 w-60" />
          <NumberInput label="Min reduction EUR" value={minWriteDown} onChange={setMinWriteDown} />
          <Toggle label="No own data only" on={noOwnData} onChange={setNoOwnData} title="Adwin holds no operator-backend or manual figure for the account" />
          <Toggle label="Nulled only" on={nulledOnly} onChange={setNulledOnly} title="Credited or restated to nil after being reported" />
          <div className="flex-1" />
          <ExportButton onClick={exportRows} label="Export" />
          <ShareButton count={shown.length} onClick={() => onShare({
            rows: shown.map((x) => x.row),
            flags: flagsFor(shown.map((x) => x.row), access),
            focus: {
              kind: 'write_downs',
              title: 'Accounts Raketech reported, Adwin invoiced on, and Raketech later reduced',
              note: noOwnData
                ? 'For each account below, Raketech\'s monthly reports stated a figure, Adwin invoiced on it, and Raketech\'s later files — the June 2025 reconciliation and the July 2026 report — carry less, in some cases nil. Adwin holds no operator-backend or manual figure for these accounts, so the only evidence either way is Raketech\'s. Adwin asks, per account, for the source data behind each reduction. Raketech\'s own comment column is reproduced where it exists.'
                : 'For each account below, Raketech\'s monthly reports stated a figure, Adwin invoiced on it, and Raketech\'s later files — the June 2025 reconciliation and the July 2026 report — carry less, in some cases nil. Adwin asks, per account, for the source data behind each reduction. Raketech\'s own comment column is reproduced where it exists.',
              selection: selection(),
            },
          })} />
        </div>

        <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(225,15%,12%)]">
                <tr>
                  <Th />
                  <SortTh label="Account" k="account" sort={sort as never} />
                  <Th>Partner / brand</Th>
                  <SortTh label="Invoiced Mar 23 – Nov 24" k="invoiced" sort={sort as never} right />
                  <SortTh label="Reported at the time" k="reported" sort={sort as never} right />
                  <Th right>June 2025 credited</Th>
                  <Th right>July 2026 report</Th>
                  <Th right>Dec 24 on: invoiced / July</Th>
                  <SortTh label="Reduction" k="writeDown" sort={sort as never} right />
                  <SortTh label="%" k="pct" sort={sort as never} right />
                  <Th right>Difference, whole period</Th>
                  <Th>Raketech's own words</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                {shown.map((x) => (
                  <React.Fragment key={x.row.key}>
                    <tr className="hover:bg-[hsl(225,15%,12%)] cursor-pointer align-top" onClick={() => toggleOpen(x.row.key)}>
                      <td className="px-3 py-2 text-zinc-500 w-6">{open.has(x.row.key) ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</td>
                      <td className="px-3 py-2 text-zinc-300 tabular-nums">{x.row.accountId}<div className="text-[10px] text-zinc-600">PA {paOf(x.row)}</div></td>
                      <td className="px-3 py-2"><div className="text-zinc-200">{x.row.brands.slice(0, 2).join(' / ') || '—'}</div><div className="text-[10px] text-zinc-500">{x.row.partner}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{x.row.invoicedRecon ? fmtEUR(x.row.invoicedRecon) : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{x.reported ? fmtEUR(x.reported) : '—'}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${x.reportedToJune > 1 ? 'text-red-400' : 'text-zinc-400'}`}>{x.row.inJuneRecon ? fmtEUR(x.june) : '—'}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${x.juneToJuly > 1 ? 'text-red-400' : 'text-zinc-400'}`}>{x.row.offers.length ? fmtEUR(x.july) : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400 whitespace-nowrap">
                        {x.row.invoicedPostRecon || x.row.earnedTail ? <>{fmtEUR(x.row.invoicedPostRecon)} / <span className={x.tail > 0 ? 'text-red-400' : ''}>{fmtEUR(x.row.earnedTail)}</span></> : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-red-400">{fmtEUR(x.writeDown)}<div className="text-[10px] text-zinc-600">{[x.reportedToJune > 1 ? `R→J ${fmtEUR(x.reportedToJune)}` : '', x.juneToJuly > 1 ? `J→Jul ${fmtEUR(x.juneToJuly)}` : '', x.tail > 0 ? `Dec 24 on ${fmtEUR(x.tail)}` : ''].filter(Boolean).join(' · ')}</div></td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{x.pct ? `${Math.round(x.pct * 100)}%` : '—'}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${x.row.underpaid < -1 ? 'text-red-400' : x.row.underpaid > 1 ? 'text-amber-400' : 'text-zinc-600'}`} title="Their figure less Adwin's invoicing, all months">{fmtEUR(-x.row.underpaid)}</td>
                      <td className="px-3 py-2 text-[10px] text-zinc-500 max-w-[260px]">
                        <div className="flex flex-wrap gap-1 mb-1">
                          {x.nulled && <Tag tone="red">nulled</Tag>}
                          {x.ownData ? <Tag tone="emerald" title="Adwin holds an operator-backend or manual figure">own data</Tag> : <Tag tone="zinc" title="Only Raketech's figures exist for this account">no own data</Tag>}
                          {noAccess(x.info) && <Tag tone="red" title={VERIFY_LABELS.no_login.long}>no login</Tag>}
                        </div>
                        {raketechComments(x.row).join(' · ') || '—'}
                      </td>
                    </tr>
                    {open.has(x.row.key) && (
                      <tr className="bg-[hsl(225,15%,8%)]"><td /><td colSpan={11} className="px-3 py-2">
                        <MonthDetail row={x.row} columns={['invoiced', 'reported', 'june', 'restated']} />
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
                {shown.length === 0 && <tr><td colSpan={12} className="px-3 py-6 text-center text-zinc-500">No accounts match the filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };
